import { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

interface BreadcrumbItem {
  title: string;
  href?: string;
}

interface BreadcrumbRoute {
  pattern: string;
  crumbs: { title: string; href?: string }[];
}

const BREADCRUMB_ROUTES: BreadcrumbRoute[] = [
  // Home
  { pattern: '/', crumbs: [{ title: 'Dashboard' }] },

  // Inspeções
  { pattern: '/request-inspection', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Inspeções' },
    { title: 'Criar Inspeção' },
  ]},
  { pattern: '/dashboard/:cardType', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Inspeções' },
    { title: 'Listar Inspeções' },
  ]},

  // Calendário
  { pattern: '/calendar', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Calendário de Vistorias' },
  ]},

  // Métricas
  { pattern: '/metrics', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Métricas' },
  ]},

  // Checklists
  { pattern: '/checklists', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Checklists' },
  ]},
  { pattern: '/checklist/new', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Checklists', href: '/checklists' },
    { title: 'Novo Checklist' },
  ]},
  { pattern: '/checklist/edit/:id', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Checklists', href: '/checklists' },
    { title: 'Editar Checklist' },
  ]},

  // Cadastros > Usuários > Banco
  { pattern: '/bank-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Banco' },
  ]},
  { pattern: '/bank-edit/:id', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Banco', href: '/bank-list' },
    { title: 'Editar' },
  ]},
  { pattern: '/bank-register', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Banco', href: '/bank-list' },
    { title: 'Novo' },
  ]},

  // Cadastros > Usuários > Prestadora (usuários)
  { pattern: '/provider-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Prestadora' },
  ]},
  { pattern: '/provider-edit/:id', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Prestadora', href: '/provider-list' },
    { title: 'Editar' },
  ]},
  { pattern: '/provider-register', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Prestadora', href: '/provider-list' },
    { title: 'Novo' },
  ]},

  // Cadastros > Usuários > Inspetor
  { pattern: '/inspector-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Inspetor' },
  ]},
  { pattern: '/inspector-edit/:id', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Inspetor', href: '/inspector-list' },
    { title: 'Editar' },
  ]},
  { pattern: '/inspector-register', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Usuários' },
    { title: 'Inspetor', href: '/inspector-list' },
    { title: 'Novo' },
  ]},

  // Cadastros > Prestadora (empresa)
  { pattern: '/vendor-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Prestadora' },
  ]},
  { pattern: '/vendor-edit/:id', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Prestadora', href: '/vendor-list' },
    { title: 'Editar' },
  ]},
  { pattern: '/vendor-register', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Prestadora', href: '/vendor-list' },
    { title: 'Nova' },
  ]},

  // Cadastros > Parametrizações
  { pattern: '/inspection-type-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Parametrizações' },
    { title: 'Tipo de Inspeção' },
  ]},
  { pattern: '/property-type-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Parametrizações' },
    { title: 'Tipo de Imóvel' },
  ]},
  { pattern: '/inspection-reason-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Parametrizações' },
    { title: 'Motivo da Inspeção' },
  ]},
  { pattern: '/inspection-issue-type-list', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Cadastros' },
    { title: 'Parametrizações' },
    { title: 'Tipos de Pendência' },
  ]},

  // Perfil
  { pattern: '/profile', crumbs: [
    { title: 'Dashboard', href: '/' },
    { title: 'Perfil' },
  ]},
];

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();

  return useMemo(() => {
    for (const route of BREADCRUMB_ROUTES) {
      if (matchPath(route.pattern, location.pathname)) {
        return route.crumbs;
      }
    }
    return [{ title: 'Dashboard', href: '/' }];
  }, [location.pathname]);
}
